import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import debounce from 'lodash.debounce';
import { navigate } from 'gatsby';
import ReCAPTCHA from 'react-google-recaptcha';
import Logo from '../../../images/BrandworkzLogoLandscape.svg';

import PageTitle from '../pageTitle/pageTitle';
import UserInstructions from '../userInstructions/userInstructions';
import FormField from '../../common/formField/formField';
import Input from '../../common/input/input';
import Button from '../../common/button/button';
import Checkbox from '../../common/checkbox/checkbox';
import FormValidationMessage from '../../common/formValidationMessage/formValidationMessage';

import { request, endpoints, methods } from '../../../lib/api';

import {
  BRANDWORKZ_MARKETING_WEBSITE_DOMAIN,
  DEBOUNCE_WAIT_TIME,
} from '../../../variables/globals';

const PLAN_ID = process.env.STRIPE_PLAN_ID;

const YourAccount = () => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const values = useSelector(state => state.yourAccountReducer.formFields);

  const handleChange = payload => {
    dispatch({ type: 'UPDATE_STATE', payload });
  };

  const handleOrganisationChange = debounce(
    (name, value, validationType, maxCharacters) => {
      handleChange({
        name,
        value,
        validationType,
        maxCharacters,
      });
    },
    DEBOUNCE_WAIT_TIME
  );

  const handleWebsiteChange = debounce(
    (name, value, validationType, maxCharacters) => {
      handleChange({ name, value, validationType, maxCharacters });
    },
    DEBOUNCE_WAIT_TIME
  );

  const handleRecaptaChange = token => {
    handleChange({ name: 'recaptcha', value: token });
  };

  const handleTermsAndConditionsChange = (name, value, validationType) => {
    handleChange({ name, value, validationType });
  };

  const handleNext = useCallback(() => {
    const formHasErrors = Object.keys(values).reduce((hasErrors, field) => {
      const trimmedValue =
        typeof values[field].value === 'string'
          ? values[field].value.trim()
          : values[field].value;

      if (!trimmedValue && values[field].isRequired) {
        const errorMessage =
          field === 'termsAndConditions'
            ? 'Please indicate that you agree to the Terms and Conditions to proceed'
            : `${values[field].displayName} is required`;

        const payload = { name: field, errors: [errorMessage] };

        dispatch({ type: 'UPDATE_ERROR_STATE', payload });
        hasErrors = true;
      }

      if (Object.keys(values).some(value => values[value].errors.length > 0)) {
        hasErrors = true;
      }

      return hasErrors;
    }, false);

    if (!formHasErrors) {
      setIsSubmitting(true);

      request(
        endpoints.instanceCandidates,
        methods.POST,
        {
          organisation: values.organisation.value,
          website: values.website.value,
          selectedPlan: PLAN_ID,
        },
        { 'x-recaptcha-secret': values.recaptcha.value }
      )
        .then(res => {
          if (res.status === 201) {
            navigate(`/create-account?c=${res.data.data.id}`);
          }
        })
        .catch(err => {
          setIsSubmitting(false);
          const payload = {
            name: 'email',
            errors: [err?.response?.data?.error ?? 'Something went wrong'],
          };
          dispatch({ type: 'UPDATE_ERROR_STATE', payload });
        });
    }
  }, [values, dispatch]);

  return (
    <>
      <p className='already-have-account'>
        <span>Already have an account? </span>
        <a href={`${process.env.MAIN_APP_URL}/login`}>Sign In</a>
      </p>
      <a href={BRANDWORKZ_MARKETING_WEBSITE_DOMAIN}>
        <Logo className='brandworkz-logo' />
      </a>
      <PageTitle title='Sign up for free' />
      <UserInstructions instructions='What organisation do you wish to create a Brandworkz Essentials account for?' />

      <form>
        <FormField icon='Company' errors={values.organisation.errors}>
          <Input
            name='organisation'
            label='Organisation Name'
            value={values.organisation.value}
            placeholder='Organisation Name (required)'
            onChange={handleOrganisationChange}
            validationType='characterLimit'
            maxCharacters={50}
          />
        </FormField>

        <FormField icon='Website' errors={values.website.errors}>
          <Input
            name='website'
            label='Website'
            value={values.website.value}
            placeholder='Website'
            onChange={handleWebsiteChange}
            maxCharacters={100}
          />
        </FormField>
        <ReCAPTCHA
          onChange={handleRecaptaChange}
          sitekey='6Lfy6eAcAAAAALwJL4OXWhkBc8wn_UWDRjBABjk5'
        />
        <FormValidationMessage errorMessage={values.recaptcha.errors[0]} />
        <FormField
          name={'termsAndConditions'}
          label='Terms and Conditions'
          errors={values.termsAndConditions.errors}
        >
          <Checkbox
            name='termsAndConditions'
            label='Terms and Conditions'
            onChange={handleTermsAndConditionsChange}
            isChecked={values.termsAndConditions.value}
            validationType='checkbox'
          />
        </FormField>
      </form>
      <div className='form-controls'>
        <Button
          className={'continue'}
          onClick={handleNext}
          ariaLabel='next'
          isDisabled={isSubmitting}
        >
          {'Next'}
        </Button>
      </div>
    </>
  );
};

export default React.memo(YourAccount);
